import type { Post } from '../blogTypes'

export const roofArea: Post = {
  slug: 'how-to-calculate-dormer-roof-area',
  title: 'How to Calculate Dormer Roof Area (With Formulas and Examples)',
  metaTitle: 'How to Calculate Dormer Roof Area: Formulas and Worked Examples',
  description:
    'Learn how to calculate dormer roof area from width, depth and pitch. Includes the slope factor formula, cheek wall area and a worked example.',
  excerpt:
    'Roof area is the number that drives your roofing order and a big share of your cost. This guide shows the maths step by step.',
  category: 'construction-technical',
  image: 'https://images.unsplash.com/photo-1753717202576-7270cc53c164?auto=format&fit=crop&w=1200&q=75',
  imageAlt: 'An unfinished attic with exposed beams and walls',
  datePublished: '2025-02-22',
  dateModified: '2025-03-18',
  readMinutes: 6,
  blocks: [
    {
      type: 'p',
      text: 'Ordering roofing, sheathing and insulation starts with one question: how big is the surface? A dormer roof is a sloped plane, so its true area is larger than the footprint underneath it. The good news is that the maths needs only a tape measure and one square root.',
    },
    { type: 'h2', text: 'Step 1: Understand roof pitch' },
    {
      type: 'p',
      text: 'Pitch is written as **rise over run**, for example 4:12. That means the roof climbs 4 inches for every 12 inches it extends horizontally. A pitch of 4:12 is about 18.4 degrees, 6:12 is 26.6 degrees and 12:12 is a steep 45 degrees.',
    },
    { type: 'h2', text: 'Step 2: Find the rise of the dormer roof' },
    {
      type: 'p',
      text: 'Multiply the dormer depth by the pitch and divide by 12. For a dormer 8 feet deep at 4:12, rise = 8 x 4 / 12 = **2.67 feet**.',
    },
    { type: 'h2', text: 'Step 3: Find the slope length' },
    {
      type: 'p',
      text: 'Use the Pythagorean theorem. Slope length = square root of (depth squared + rise squared). Continuing the example, the square root of (64 + 7.11) = **8.43 feet**. This is the real length of the rafter, before overhang.',
    },
    { type: 'h2', text: 'Step 4: Calculate roof area' },
    {
      type: 'p',
      text: 'Roof area = width x (slope length + overhang). For a 10 foot wide dormer with a 6 inch overhang, area = 10 x (8.43 + 0.5) = **89.3 square feet**. Add **10 percent waste** for cuts and you order about 98 square feet, close to one roofing square (100 square feet).',
    },
    { type: 'h2', text: 'Step 5: Add the walls' },
    {
      type: 'p',
      text: 'The front wall is width x height. The two side walls, called cheeks, are trapezoids or triangles because the roof slopes above them. Their combined area is 2 x depth x (height + rise / 2). Cladding, sheathing and insulation need these areas too.',
    },
    {
      type: 'ul',
      items: [
        'Front wall: 10 x 4 = 40 sq ft',
        'Cheeks: 2 x 8 x (4 + 1.33) = 85.3 sq ft',
        'Roof: 89.3 sq ft',
        'Total envelope: about 214.6 sq ft',
      ],
    },
    { type: 'h2', text: 'Common measuring mistakes' },
    {
      type: 'ul',
      items: [
        'Using the footprint as the roof area and under-ordering by 5 to 30 percent.',
        'Forgetting the overhang, flashing and ridge detail.',
        'Mixing metric and imperial units mid-calculation.',
        'Skipping waste. Complex roofs need 10 to 15 percent extra.',
      ],
    },
    {
      type: 'cta',
      title: 'Skip the manual maths',
      text: 'The calculator applies these formulas instantly and converts them into a materials list and cost range.',
    },
    {
      type: 'p',
      text: 'Want to see the numbers in action? Open the [dormer calculator](/calculator) and enter the same example to check your own working. If you are still deciding on a roof style, read [shed vs gable dormer](/blog/shed-vs-gable-dormer).',
    },
  ],
}
